import { Button, Combobox, Input, Label } from "@tellescope/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn, levenshteinDistance } from "@tellescope/utils";
import { MedicationResponse } from "@tellescope/types-models";
import { UNKNOWN_DRUG_NAME } from "../../utils";
import { CanvasMedicationInstructions } from "./CanvasMedicationInstructions";
import { EMPTY_MEDICATION } from "./empty-medication";
import { MedicationDosageFields } from "./MedicationDosageFields";
import { useMedicationCatalog } from "./useMedicationCatalog";

export interface AddMedicationFormProps {
  medication?: MedicationResponse;
  onChange: (medication: MedicationResponse) => void;
  onRemove?: () => void;
  className?: string;
  dataSource?: string;
  autoFocus?: boolean;
  required?: boolean;
  fieldId?: string;
}

export const AddMedicationForm = ({
  medication: providedMedication,
  onChange,
  onRemove,
  className,
  dataSource,
  autoFocus,
  required,
  fieldId,
}: AddMedicationFormProps) => {
  const { t } = useTranslation("forms");
  const storedMedication: MedicationResponse = {
    ...providedMedication,
    displayTerm: providedMedication?.displayTerm ?? "",
    drugName: providedMedication?.drugName ?? "",
  };

  const {
    isCanvas,
    searchQuery,
    setSearchQuery,
    allDisplayNames,
    specificDrugOptions,
    rxNormCode,
    ndcCodes,
    isLoadingSpecificDrugs,
    canvasOptions,
  } = useMedicationCatalog({
    selectedDisplayName: storedMedication.displayTerm,
    selectedSpecificDrugName: storedMedication.drugName,
    dataSource,
  });

  const isDrugUnresolvable =
    !isCanvas && !!storedMedication.displayTerm && !isLoadingSpecificDrugs && !specificDrugOptions.length;

  const medication: MedicationResponse = storedMedication.drugName
    ? {
        ...storedMedication,
        rxNormCode: rxNormCode?.[0] ?? storedMedication.rxNormCode,
        NDCs: ndcCodes?.length ? ndcCodes.map(String) : storedMedication.NDCs,
      }
    : isDrugUnresolvable
    ? { ...storedMedication, drugName: UNKNOWN_DRUG_NAME }
    : storedMedication;

  const selectedSpecificDrug = useMemo(() => {
    return specificDrugOptions.find((drug) => drug.name === medication.drugName);
  }, [medication.drugName, specificDrugOptions]);

  const displayTermId = fieldId ? `${fieldId}-displayTerm` : undefined;
  const drugSelectId = fieldId ? `${fieldId}-drugName` : undefined;
  const otherDrugId = fieldId ? `${fieldId}-otherDrug` : undefined;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="space-y-1">
        <Label htmlFor={displayTermId} required={required}>
          {t("Search")}
        </Label>
        <Combobox
          id={displayTermId}
          modal
          value={medication.displayTerm ? { value: medication.displayTerm, label: medication.displayTerm } : undefined}
          onChange={(option) => {
            const displayTerm = option?.value ?? "";
            if (displayTerm === medication.displayTerm) return;
            if (!isCanvas) return onChange({ ...EMPTY_MEDICATION, displayTerm });
            const selected = canvasOptions?.find((canvasOption) => canvasOption.display === displayTerm);
            onChange({ ...EMPTY_MEDICATION, displayTerm, drugName: displayTerm, fdbCode: selected?.code });
          }}
          {...(isCanvas ? { searchQuery, setSearchQuery } : {})}
          options={allDisplayNames.map((displayName) => ({
            value: displayName,
            label: displayName,
          }))}
          alwaysFilter
          customFilter={(searchTerm, options) => {
            if (isCanvas) return options;
            if (searchTerm) {
              return [...options]
                .filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
                .sort((a, b) => a.label.length - b.label.length)
                .slice(0, 50);
            }
            return options.slice(0, 50);
          }}
          placeholder={t("Choose a medication...")}
          searchPlaceholder={t("Search medications...")}
          autoFocus={autoFocus}
          isLoading={isCanvas ? isLoadingSpecificDrugs : allDisplayNames.length === 0}
          loadingText={t("Loading medications...")}
          emptyText={t("No medications found")}
          clearable
        />
      </div>
      {!!medication.displayTerm && !medication.otherDrug && !!specificDrugOptions.length && (
        <div className="space-y-1">
          <Label htmlFor={drugSelectId} required={required}>
            {t("Drug Select")}
          </Label>
          <Combobox
            id={drugSelectId}
            modal
            value={
              medication.drugName
                ? {
                    value: medication.drugName,
                    label: selectedSpecificDrug?.synonym ?? medication.drugName,
                  }
                : undefined
            }
            onChange={(option) => {
              const selected = specificDrugOptions.find((drug) => drug.name === option?.value);
              onChange({
                ...medication,
                drugName: option?.value ?? "",
                drugSynonym: selected?.synonym,
                rxNormCode: selected ? String(selected.rxcui) : undefined,
              });
            }}
            options={specificDrugOptions.map((drug) => ({
              value: drug.name,
              label: drug.synonym,
            }))}
            alwaysFilter
            customFilter={(searchTerm, options) => {
              const getSearchableString = (option: (typeof options)[number]): string => {
                return `${option.label}|${option.value}|${option.description ?? ""}`.toLowerCase();
              };

              if (searchTerm) {
                return [...options]
                  .filter((option) => getSearchableString(option).includes(searchTerm.toLowerCase()))
                  .sort(
                    (a, b) =>
                      levenshteinDistance(getSearchableString(b), searchTerm.toLowerCase()) -
                      levenshteinDistance(getSearchableString(a), searchTerm.toLowerCase())
                  );
              }
              return [...options].slice(0, 50);
            }}
            placeholder={t("Drug Select")}
            searchPlaceholder={t("Search specific drugs...")}
            emptyText={t("No specific drugs found")}
            clearable
          />
        </div>
      )}
      {!!medication.displayTerm && (
        <>
          {(isDrugUnresolvable || !!medication.otherDrug) && (
            <div className="space-y-1">
              <Label htmlFor={otherDrugId} required>
                {t("Other Drug")}
              </Label>
              <Input
                id={otherDrugId}
                value={medication.otherDrug}
                onChange={(e) => onChange({ ...medication, otherDrug: e.target.value })}
                placeholder={t("Other Drug")}
              />
            </div>
          )}
          {isCanvas ? (
            <CanvasMedicationInstructions medication={medication} onChange={onChange} fieldId={fieldId} />
          ) : (
            <MedicationDosageFields medication={medication} onChange={onChange} fieldId={fieldId} />
          )}
          {!!onRemove && (
            <div className="w-full flex justify-end">
              <Button variant="outline" onClick={onRemove}>
                {t("Remove Medication")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
